import React, { useEffect, useState } from "react";
import {
  InformationCircleIcon,
  CheckCircleIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  LinkSlashIcon,
} from "@heroicons/react/24/outline";
import AddDSPModal from "../../modal/AddDSPModal";
import EditDSPModal from "../../modal/EditDSPModal";
import AddTeamMemberModal from "../../modal/AddTeamMemberModal";
import DataTable, { DataTableColumn, dataTableRowClass, dataTableTdClass, rowActionIconClass, rowActionDangerIconClass } from "../../ui/DataTable";
import { cn } from "../../../DashboardPage/lib/utils";
import { AddDSPData, DSPData, EditDSPData } from "@/components/types/types";
import { dspApi } from "../../../repositories/dsp";
import Swal from "sweetalert2";
import { formatDSPName } from "../../../utils/formatDSP";
import { PlusIcon } from "lucide-react";
import { formatDisplayDate } from "../../../DashboardPage/utils/dates";
import { Skeleton } from '../../ui/skeleton';
import SettingsPanel from '../../ui/SettingsPanel';
import Button from "../../ui/Button";
import { swalColors } from "../../../utils/swal";
import { buttonVariants } from "../../ui/buttonVariants";

// List of supported DSPs
const SUPPORTED_DSPS = [
  "Spotify for Artists",
  "Amazon Music for Artists",
  "Deezer for Creators",
  "Audiomack for Creators",
  "YouTube Music for Artists",
  "Apple Music for Artists",
  "Tidal for Artists",
];

const DSPs = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isAddTeamMemberModalOpen, setIsAddTeamMemberModalOpen] = useState<boolean>(false);
  const [selectedDsp, setSelectedDsp] = useState<string | null>(null);
  const [selectedTeamMemberDsp, setSelectedTeamMemberDsp] = useState<string>("");
  const [dspList, setDspList] = useState<DSPData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentDsp, setCurrentDsp] = useState<DSPData | null>(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(
    new Set()
  );

  const fetchDSPs = async () => {
    setLoading(true);
    try {
      const response = await dspApi.getDSPs();
      setDspList(response.data.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setDspList([]);
      } else {
        Swal.fire(
          "Error",
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDSPs();
  }, []);

  // Format DSP display name to the API-friendly format
  const formatDspTypeForApi = (dspName: string): string => {
    const formatted = dspName
      .toLowerCase()
      .replace(/\s+for\s+(artists|creators)$/i, "")
      .replace(/\s+/g, "_");

    // Special case: Audiomack should be stored as "audio_mack" in the API
    if (formatted === "audiomack") {
      return "audio_mack";
    }

    return formatted;
  };

  // Get all connected accounts for a specific DSP type
  const getConnectedAccounts = (dspType: string): DSPData[] => {
    const formattedType = formatDspTypeForApi(dspType);
    return dspList.filter((dsp) => dsp.type === formattedType);
  };

  // Toggle table visibility for a platform
  const togglePlatformTable = (dspName: string) => {
    const newExpanded = new Set(expandedPlatforms);
    if (newExpanded.has(dspName)) {
      newExpanded.delete(dspName);
    } else {
      newExpanded.add(dspName);
    }
    setExpandedPlatforms(newExpanded);
  };

  const handleConnect = (dspName: string) => {
    const formattedDspName = formatDspTypeForApi(dspName);
    setSelectedDsp(formattedDspName);
    setIsAddModalOpen(true);
  };

  const handleAddTeamMember = (dspName: string) => {
    setSelectedTeamMemberDsp(dspName);
    setIsAddTeamMemberModalOpen(true);
  };

  const handleEditClick = (dsp: DSPData) => {
    setCurrentDsp(dsp);
    setIsEditModalOpen(true);
  };

  const handleDeleteDsp = async (dspId: string, dspType: string) => {
    try {
      const dspIdNumber = Number(dspId);
      if (isNaN(dspIdNumber)) throw new Error("Invalid DSP ID");

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: swalColors.danger,
        confirmButtonText: "Yes, disconnect",
      });

      if (result.isConfirmed) {
        await dspApi.deleteDSP(dspIdNumber, dspType);
        setDspList((prev) =>
          prev.filter((dsp) => Number(dsp.id) !== dspIdNumber)
        );
        Swal.fire(
          "Disconnected!",
          "Your DSP connection has been disconnected.",
          "success"
        );
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred",
        "error"
      );
    }
  };

  const handleAddDSP = async (dspData: AddDSPData): Promise<void> => {
    try {
      await dspApi.addDSP(dspData);
      Swal.fire("Success", "DSP connected successfully", "success");
      setIsAddModalOpen(false);
      fetchDSPs();
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred",
        "error"
      );
    }
  };

  const handleEditDsp = async (dspData: EditDSPData): Promise<void> => {
    try {
      if (currentDsp) {
        await dspApi.updateDSP(currentDsp.id, dspData);
        Swal.fire("Success", "DSP updated successfully", "success");
        setIsEditModalOpen(false);
        setCurrentDsp(null);
        fetchDSPs();
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred",
        "error"
      );
    }
  };

  // One style for the small secondary actions on this tab (Connect / View
  // Accounts), which previously used two different arbitrary pill colours.
  const secondaryPillClass = cn(buttonVariants({ variant: "secondary" }), "shrink-0 whitespace-nowrap");

  // Header only — rows are composed below so each keeps its action buttons.
  const accountColumns: DataTableColumn<any>[] = [
    { key: "index", header: "#" },
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "connectedDate", header: "Connected Date" },
    { key: "status", header: "Status" },
    { key: "actions", header: "Actions", align: "center" },
  ];

  return (
    <SettingsPanel
      title="Connected DSP Accounts"
      description="Link your DSP accounts to pitch directly from Cadence"
      divided
    >

      {/* Explanation Section */}
      <div className="flex items-start p-3 mb-4 border rounded-lg bg-primary-50 border-primary-200 max-sm:p-2 sm:mb-6">
        <InformationCircleIcon className="flex-shrink-0 w-5 h-5 mr-2 text-primary-700 sm:w-6 sm:h-6" />
        <div>
          <p className="text-sm text-neutral-600 max-sm:text-xs">
            Connect to DSPs to fetch streaming data and improve pitch targeting.{" "}
          </p>
          <p className="text-sm text-neutral-600 max-sm:text-xs">
            Connections refresh automatically every 7 days.{" "}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-6" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="overflow-hidden border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between p-4 border-b border-neutral-100 bg-neutral-50">
                <Skeleton className="w-48 h-5" />
                <Skeleton className="w-24 h-8" />
              </div>
              <div className="p-6">
                <Skeleton className="w-64 h-4" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* DSP Platforms */
        <div className="space-y-6" data-tour="dsp-credentials-list">
          {SUPPORTED_DSPS.map((dspName) => {
            const connectedAccounts = getConnectedAccounts(dspName);
            const accountCount = connectedAccounts.length;
            const isExpanded = expandedPlatforms.has(dspName);

            return (
              <div
                key={dspName}
                className="overflow-hidden border rounded-lg border-neutral-200"
              >
                {/* Platform Header */}
                <div className="p-4 border-b border-neutral-100 bg-neutral-50">
                  {/*
                    The rows differ only in how long the platform name is, so the
                    layout has to absorb that difference rather than let it
                    reshape the row: the title group wraps and shrinks, while the
                    actions keep their natural width on one line. Without this a
                    longer name ("Amazon Music for Artists") squeezed the buttons
                    until their labels wrapped, and that row alone sat taller
                    than the rest.
                  */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center min-w-0 gap-3">
                      <h3 className="text-base font-semibold text-black sm:text-lg">
                        {dspName}
                      </h3>
                      {accountCount > 0 && (
                        <span className="px-3 py-1 text-xs font-medium text-success-800 border border-success-200 rounded-full bg-success-50 whitespace-nowrap">
                          {accountCount} account{accountCount !== 1 ? "s" : ""}{" "}
                          connected
                        </span>
                      )}
                      {accountCount === 0 && (
                        <span className="px-3 py-1 text-xs font-medium border rounded-full text-neutral-700 bg-neutral-100 border-neutral-200 whitespace-nowrap">
                          Not connected
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Add Team Member Button - shown only for Spotify and Amazon when accounts exist */}
                      {accountCount > 0 &&
                       (dspName === "Spotify for Artists" || dspName === "Amazon Music for Artists") && (
                        <Button
                          onClick={() => handleAddTeamMember(dspName)}
                          className="shrink-0"
                        >
                          Add Team Member
                        </Button>
                      )}

                      {/* Connect Button - always shown */}
                      <button
                        onClick={() => handleConnect(dspName)}
                        className={secondaryPillClass}
                        data-tour={dspName === "Spotify for Artists" ? "spotify-connect-btn" : undefined}
                      >
                        <PlusIcon className="w-4 h-4 shrink-0" />
                        Connect
                      </button>

                      {/* View Accounts Button */}
                      {accountCount > 0 && (
                        <button
                          onClick={() => togglePlatformTable(dspName)}
                          className={secondaryPillClass}
                        >
                          <EyeIcon className="w-4 h-4 shrink-0" />
                          {isExpanded ? "Hide" : "View"} Accounts
                          {isExpanded ? (
                            <ChevronUpIcon className="w-4 h-4 shrink-0" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4 shrink-0" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Accounts Table */}
                {accountCount > 0 && isExpanded && (
                  <div className="p-4">
                      <DataTable columns={accountColumns}>
                          {connectedAccounts.map((account, index) => (
                            <tr key={account.id} className={dataTableRowClass}>
                              <td className={cn(dataTableTdClass, 'text-neutral-600')}>
                                {index + 1}
                              </td>
                              <td className={dataTableTdClass}>
                                  <div className="flex flex-wrap gap-1">
                                    {account.name
                                      .split(',')
                                      .filter(artist => artist.trim() !== '')
                                      .map((artist, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full text-primary-700 bg-primary-50 border-primary-200"
                                          aria-label={`Artist: ${artist.trim()}`}
                                        >
                                          {artist.trim()}
                                        </span>
                                      ))}
                                  </div>
                                
                              </td>
                              <td className={dataTableTdClass}>
                                <div className="text-neutral-600">
                                  {account.email}
                                </div>
                              </td>

                              <td className={dataTableTdClass}>
                                <div className="text-neutral-600">
                                  {formatDisplayDate(account.createdAt)}
                                </div>
                              </td>

                              <td className={dataTableTdClass}>
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-success-800 bg-success-50 border border-success-200 rounded-full">
                                  <CheckCircleIcon className="w-3 h-3" />
                                  Active
                                </span>
                              </td>
                              <td className={dataTableTdClass}>
                                <div className="flex items-center justify-center gap-1">
                                  {/* Edit Button */}
                                  <button
                                    onClick={() => handleEditClick(account)}
                                    className={rowActionIconClass}
                                    title="Edit account"
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                  </button>

                                  {/* Delete Button */}
                                  <button
                                    onClick={() =>
                                      handleDeleteDsp(account.id, account.type)
                                    }
                                    className={rowActionDangerIconClass}
                                    title="Disconnect account"
                                  >
                                    <LinkSlashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </DataTable>
                  </div>
                )}

                {/* No Accounts Message */}
                {accountCount === 0 && (
                  <div className="p-6 text-center text-neutral-500">
                    <div className="mb-2">
                      <InformationCircleIcon className="w-12 h-12 mx-auto text-neutral-300" />
                    </div>
                    <p className="mb-1 text-sm font-medium">
                      No accounts connected
                    </p>
                    <p className="text-xs">
                      Connect your {dspName} account to get started with data
                      syncing.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary. Placeholder band while loading, so the page doesn't grow when it lands. */}
      {loading && <Skeleton className="h-[74px] mt-6 rounded-lg" />}
      {!loading && dspList.length > 0 && (
        <div className="p-4 mt-6 border rounded-lg border-primary-200 bg-primary-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-primary-900">
                Total Connected Accounts
              </h4>
              <p className="text-2xl font-bold text-primary-900">
                {dspList.length}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-primary-700">
                Across{" "}
                {
                  SUPPORTED_DSPS.filter(
                    (dsp) => getConnectedAccounts(dsp).length > 0
                  ).length
                }{" "}
                platforms
              </p>
              <p className="text-xs text-primary-700">
                Data syncing automatically
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add DSP Modal */}
      <AddDSPModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddDSP}
        dspData={null}
        selectedDSP={selectedDsp}
      />

      {/* Edit DSP Modal */}
      <EditDSPModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditDsp}
        dspData={
          currentDsp || {
            id: "",
            type: "",
            name: "",
            email: "",
            password: "",
            createdAt: "",
          }
        }
      />

      {/* Add Team Member Modal */}
      <AddTeamMemberModal
        isOpen={isAddTeamMemberModalOpen}
        onClose={() => setIsAddTeamMemberModalOpen(false)}
        dspName={selectedTeamMemberDsp}
      />
    </SettingsPanel>
  );
};

export default DSPs;
